import { useRef, useState, type ReactNode, type PointerEvent as ReactPointerEvent } from 'react';
import { tapLight, tapMedium } from '../lib/haptics';

interface SwipeableRowProps {
  children: ReactNode;
  /** Swipe right → complete. */
  onComplete?: () => void;
  /** Swipe left → skip. */
  onSkip?: () => void;
  completeLabel?: string;
  skipLabel?: string;
  disabled?: boolean;
  /** Extra classes on the outer .swipe-row element (e.g. chore-card). */
  className?: string;
}

const THRESHOLD = 96; // px of travel to arm an action
const MAX = 132;       // px the content can be dragged

/**
 * A row that reveals coloured action backgrounds as it's dragged horizontally.
 * Drag past the threshold and release to fire the action. Vertical scrolling is
 * preserved (`touch-action: pan-y` on `.swipe-row`); we only hijack the gesture
 * once it's clearly horizontal.
 */
export function SwipeableRow({
  children,
  onComplete,
  onSkip,
  completeLabel = 'Done',
  skipLabel = 'Skip',
  disabled = false,
  className,
}: SwipeableRowProps) {
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const horizontal = useRef(false);
  const armed = useRef(false);

  const canRight = !!onComplete;
  const canLeft = !!onSkip;

  const onPointerDown = (e: ReactPointerEvent) => {
    if (disabled || e.pointerType === 'mouse') return; // touch/pen only
    start.current = { x: e.clientX, y: e.clientY };
    horizontal.current = false;
    armed.current = false;
    setAnimating(false);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    if (!start.current || disabled) return;
    const deltaX = e.clientX - start.current.x;
    const deltaY = e.clientY - start.current.y;

    if (!horizontal.current) {
      if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        // Vertical intent — let the page scroll, abandon this gesture.
        start.current = null;
        return;
      }
      horizontal.current = true;
      (e.target as Element).setPointerCapture?.(e.pointerId);
    }

    // Clamp + resist in directions with no action
    let next = deltaX;
    if ((next > 0 && !canRight) || (next < 0 && !canLeft)) next *= 0.25;
    next = Math.max(-MAX, Math.min(MAX, next));
    setDx(next);

    const past = Math.abs(next) >= THRESHOLD && ((next > 0 && canRight) || (next < 0 && canLeft));
    if (past && !armed.current) {
      armed.current = true;
      tapLight();
    } else if (!past && armed.current) {
      armed.current = false;
    }
  };

  const settle = () => {
    setAnimating(true);
    setDx(0);
  };

  const onPointerUp = () => {
    if (!start.current && !horizontal.current) {
      start.current = null;
      return;
    }
    const armedNow = armed.current;
    const dir = dx;
    start.current = null;
    horizontal.current = false;
    armed.current = false;

    if (armedNow && dir > 0 && onComplete) {
      tapMedium();
      // fling off-screen, then fire
      setAnimating(true);
      setDx(window.innerWidth);
      setTimeout(onComplete, 180);
      return;
    }
    if (armedNow && dir < 0 && onSkip) {
      tapMedium();
      setAnimating(true);
      setDx(-window.innerWidth);
      setTimeout(onSkip, 180);
      return;
    }
    settle();
  };

  const reveal = Math.abs(dx);
  const showComplete = dx > 0;
  const showSkip = dx < 0;
  const armedVisual = reveal >= THRESHOLD;

  return (
    <div className={className ? `swipe-row ${className}` : 'swipe-row'}>
      {(canRight || canLeft) && reveal > 2 && (
        <div className="swipe-actions" aria-hidden="true">
          {showComplete && canRight ? (
            <div
              className="swipe-action swipe-action-complete"
              style={{ flex: 1, opacity: armedVisual ? 1 : 0.7 }}
            >
              <ion-icon name="checkmark-circle" />
              <span style={{ transform: armedVisual ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s' }}>
                {completeLabel}
              </span>
            </div>
          ) : <div style={{ flex: 1 }} />}
          {showSkip && canLeft && (
            <div
              className="swipe-action swipe-action-skip"
              style={{ opacity: armedVisual ? 1 : 0.7 }}
            >
              <span style={{ transform: armedVisual ? 'scale(1.08)' : 'scale(1)', transition: 'transform 0.15s' }}>
                {skipLabel}
              </span>
              <ion-icon name="play-skip-forward-circle" />
            </div>
          )}
        </div>
      )}
      <div
        className="swipe-content"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? 'transform 0.28s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
